import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, ShoppingCart, Music, Check, Lock, Headphones } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SubscriptionPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentType: 'song' | 'video';
  contentTitle: string;
  contentArtist?: string;
  contentPrice?: number;
  contentCover?: string;
  onPurchase?: () => void;
}

export function SubscriptionPromptModal({
  isOpen,
  onClose,
  contentType,
  contentTitle,
  contentArtist,
  contentPrice = 0.99,
  contentCover,
  onPurchase
}: SubscriptionPromptModalProps) {
  const navigate = useNavigate();

  const handleSubscribe = () => {
    onClose();
    navigate('/subscription');
  };

  const handlePurchase = () => {
    if (onPurchase) {
      onPurchase();
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        >
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
            onClick={onClose}
          />

          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-stone-100 rounded-full transition-colors z-10"
            >
              <X className="w-5 h-5 text-royal-text-secondary" />
            </button>

            <div className="p-8">
              <div className="flex items-center gap-4 mb-6">
                {contentCover ? (
                  <img
                    src={contentCover}
                    alt={contentTitle}
                    className="w-20 h-20 rounded-xl object-cover shadow-lg"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-royal-gold to-royal-gold-dark flex items-center justify-center">
                    <Music className="w-10 h-10 text-white" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Lock className="w-4 h-4 text-royal-gold" />
                    <span className="text-sm font-medium text-royal-gold">Preview Ended</span>
                  </div>
                  <h3 className="text-xl font-bold text-royal-text">{contentTitle}</h3>
                  {contentArtist && (
                    <p className="text-royal-text-secondary">{contentArtist}</p>
                  )}
                </div>
              </div>

              <div className="text-center mb-8">
                <p className="text-royal-text-secondary">
                  Your 30-second preview has ended. Choose how you want to continue listening:
                </p>
              </div>

              <div className="space-y-4">
                <div className="p-5 rounded-2xl bg-gradient-to-r from-royal-gold/10 to-royal-gold/5 border border-royal-gold/20">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-royal-gold flex items-center justify-center">
                      <Crown className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-royal-text">VISION Music Unlimited</h4>
                      <p className="text-sm text-royal-text-secondary">$9.99/month</p>
                    </div>
                  </div>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center gap-2 text-sm text-royal-text">
                      <Check className="w-4 h-4 text-emerald-500" />
                      Unlimited streaming of all songs and videos
                    </li>
                    <li className="flex items-center gap-2 text-sm text-royal-text">
                      <Check className="w-4 h-4 text-emerald-500" />
                      Ad-free listening experience
                    </li>
                    <li className="flex items-center gap-2 text-sm text-royal-text">
                      <Check className="w-4 h-4 text-emerald-500" />
                      Exclusive artist content and early releases
                    </li>
                    <li className="flex items-center gap-2 text-sm text-royal-text">
                      <Check className="w-4 h-4 text-emerald-500" />
                      Offline downloads
                    </li>
                  </ul>
                  <button
                    onClick={handleSubscribe}
                    className="w-full py-3.5 bg-royal-gold text-white rounded-xl font-semibold hover:bg-royal-gold-dark transition-colors flex items-center justify-center gap-2"
                  >
                    <Headphones className="w-5 h-5" />
                    Subscribe to Unlimited
                  </button>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-stone-200"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-4 bg-white text-sm text-royal-text-secondary">or</span>
                  </div>
                </div>

                <button
                  onClick={handlePurchase}
                  className="w-full py-3.5 bg-white text-royal-text rounded-xl font-semibold border border-stone-200 hover:border-royal-gold/30 hover:bg-stone-50 transition-all flex items-center justify-center gap-2"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Buy this {contentType} for ${contentPrice.toFixed(2)}
                </button>

                <p className="text-center text-xs text-royal-text-secondary">
                  One-time purchase. Download and keep forever.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
